import type { DbaAccountDetail, DbaAccountMember } from '@vivport/backend/types/dba';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { apiErrorMessage } from '@/api/errors';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { StripeWarning } from '@/components/stripe-warning';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Notice } from '@/components/ui/notice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  useActivateComplimentary,
  useEndComplimentary,
  useGrantCredit,
  useInviteHubMember,
  useRemoveDiscount,
  useResendInvite,
  useSetDiscount,
} from '@/hooks/use-dba-mutations';
import { useAccount } from '@/hooks/use-dba-queries';
import { formatAud, formatDateTime } from '@/lib/format';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function NoteField({ id, value, onChange }: { id: string; value: string; onChange: (value: string) => void }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Note</Label>
      <Textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} required />
    </div>
  );
}

function accountLabel(account: DbaAccountDetail): string {
  return `${account.accountName} (${account.email})`;
}

function SetDiscountLever({ account }: { account: DbaAccountDetail }) {
  const setDiscount = useSetDiscount();
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');
  const percentOff = Number(percent);
  return (
    <ConfirmDialog
      triggerLabel="Set discount"
      title="Set discount"
      description={`Sets the checkout discount for ${account.email} on ${accountLabel(account)}. It applies to future checkouts. 100% skips Stripe: unless the account already pays through Stripe, it activates the subscription now and grants the first-month credit, and top-ups become free grants.`}
      confirmLabel="Set discount"
      canConfirm={Number.isInteger(percentOff) && percentOff >= 1 && percentOff <= 100}
      onOpenChange={(open) => {
        if (open) {
          setPercent(account.billing.discountPercent > 0 ? String(account.billing.discountPercent) : '');
          setNote(account.billing.discountNote ?? '');
        }
      }}
      onConfirm={() =>
        setDiscount.mutateAsync({
          email: account.email,
          body: { percentOff, note: note.trim() || undefined },
        })
      }
    >
      {!account.billing.discountAppliesToExistingSubscription && <StripeWarning />}
      <div className="space-y-2">
        <Label htmlFor="discount-percent">Percent off</Label>
        <Input
          id="discount-percent"
          type="number"
          min={1}
          max={100}
          step={1}
          value={percent}
          onChange={(event) => setPercent(event.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="discount-note">Note</Label>
        <Textarea id="discount-note" value={note} onChange={(event) => setNote(event.target.value)} />
      </div>
    </ConfirmDialog>
  );
}

function RemoveDiscountLever({ account }: { account: DbaAccountDetail }) {
  const removeDiscount = useRemoveDiscount();
  return (
    <ConfirmDialog
      triggerLabel="Remove discount"
      title="Remove discount"
      description={`Removes the ${account.billing.discountPercent}% discount for ${account.email} on ${accountLabel(account)}. Future checkouts charge the full price.${account.billing.arrangement === 'COMPLIMENTARY' ? ' Complimentary access stays until you end it.' : ''}`}
      confirmLabel="Remove discount"
      confirmVariant="destructive"
      onConfirm={() => removeDiscount.mutateAsync(account.email)}
    >
      {!account.billing.discountAppliesToExistingSubscription && <StripeWarning />}
    </ConfirmDialog>
  );
}

function ActivateComplimentaryLever({ account }: { account: DbaAccountDetail }) {
  const activate = useActivateComplimentary();
  const [note, setNote] = useState('');
  return (
    <ConfirmDialog
      triggerLabel="Activate complimentary"
      title="Activate complimentary"
      description={`Gives ${accountLabel(account)} an active subscription with no Stripe billing. The account stops being sent to the subscribe page.`}
      confirmLabel="Activate"
      canConfirm={note.trim().length > 0}
      onOpenChange={(open) => open && setNote('')}
      onConfirm={() => activate.mutateAsync({ accountId: account.id, body: { note } })}
    >
      <NoteField id="activate-note" value={note} onChange={setNote} />
    </ConfirmDialog>
  );
}

function EndComplimentaryLever({ account }: { account: DbaAccountDetail }) {
  const end = useEndComplimentary();
  const [note, setNote] = useState('');
  return (
    <ConfirmDialog
      triggerLabel="End complimentary"
      title="End complimentary"
      description={`Cancels the complimentary subscription of ${accountLabel(account)}. On their next page load the members land on the subscribe page and must pay to continue.`}
      confirmLabel="End complimentary"
      confirmVariant="destructive"
      canConfirm={note.trim().length > 0}
      onOpenChange={(open) => open && setNote('')}
      onConfirm={() => end.mutateAsync({ accountId: account.id, body: { note } })}
    >
      <NoteField id="end-note" value={note} onChange={setNote} />
    </ConfirmDialog>
  );
}

function GrantCreditLever({ account }: { account: DbaAccountDetail }) {
  const grant = useGrantCredit();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const amountAud = Number(amount);
  return (
    <ConfirmDialog
      triggerLabel="Grant credit"
      title="Grant credit"
      description={`Adds a promotional wallet credit to ${accountLabel(account)}. The current balance is ${formatAud(account.billing.balanceAud)}.`}
      confirmLabel="Grant credit"
      canConfirm={amountAud > 0 && note.trim().length > 0}
      onOpenChange={(open) => {
        if (open) {
          setAmount('');
          setNote('');
        }
      }}
      onConfirm={() => grant.mutateAsync({ accountId: account.id, body: { amountAud, note } })}
    >
      <div className="space-y-2">
        <Label htmlFor="credit-amount">Amount (AUD)</Label>
        <Input
          id="credit-amount"
          type="number"
          min={0.01}
          step={0.01}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          required
        />
      </div>
      <NoteField id="credit-note" value={note} onChange={setNote} />
    </ConfirmDialog>
  );
}

function InviteMemberLever({ account, hubId, hubName }: { account: DbaAccountDetail; hubId: number; hubName: string }) {
  const invite = useInviteHubMember();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  return (
    <ConfirmDialog
      triggerLabel="Invite member"
      title={`Invite member to ${hubName}`}
      description={`Creates a member of ${hubName}, owned by ${accountLabel(account)}, and emails them an invite to set their own password.`}
      confirmLabel="Send invite"
      canConfirm={email.trim().length > 0 && firstName.trim().length > 0 && surname.trim().length > 0}
      onOpenChange={(open) => {
        if (open) {
          setEmail('');
          setFirstName('');
          setSurname('');
        }
      }}
      onConfirm={() => invite.mutateAsync({ hubId, body: { email, firstName, surname } })}
    >
      <div className="space-y-2">
        <Label htmlFor={`invite-email-${hubId}`}>Email</Label>
        <Input
          id={`invite-email-${hubId}`}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`invite-first-${hubId}`}>First name</Label>
          <Input
            id={`invite-first-${hubId}`}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`invite-surname-${hubId}`}>Surname</Label>
          <Input
            id={`invite-surname-${hubId}`}
            value={surname}
            onChange={(event) => setSurname(event.target.value)}
            required
          />
        </div>
      </div>
    </ConfirmDialog>
  );
}

function ResendInviteLever({ account, member }: { account: DbaAccountDetail; member: DbaAccountMember }) {
  const resend = useResendInvite();
  return (
    <ConfirmDialog
      triggerLabel="Resend invite"
      title="Resend invite"
      description={`Emails a fresh invite link to ${member.email} for ${member.hubName} on ${accountLabel(account)}.`}
      confirmLabel="Resend invite"
      onConfirm={() => resend.mutateAsync(member.memberId)}
    />
  );
}

interface HubGroup {
  hubId: number;
  hubName: string;
  isPersonal: boolean;
  members: DbaAccountMember[];
}

function groupByHub(members: DbaAccountMember[]): HubGroup[] {
  const groups = new Map<number, HubGroup>();
  for (const member of members) {
    const group = groups.get(member.hubId) ?? {
      hubId: member.hubId,
      hubName: member.hubName,
      isPersonal: member.isPersonal,
      members: [],
    };
    group.members.push(member);
    groups.set(member.hubId, group);
  }
  return [...groups.values()];
}

const arrangementVariant = {
  STRIPE: 'info',
  COMPLIMENTARY: 'success',
  NONE: 'warning',
} as const;

function AccountDetailView({ account }: { account: DbaAccountDetail }) {
  const { billing } = account;
  return (
    <>
      <div className="flex items-center gap-3">
        <Link to="/accounts" className="text-sm text-muted-foreground hover:text-foreground">
          Accounts
        </Link>
        <span className="text-sm text-muted-foreground">/</span>
        <h1 className="text-xl font-medium">{account.accountName}</h1>
        {account.isOperator && <Badge variant="info">Operator</Badge>}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Field label="Name">
              {account.firstName} {account.surname}
            </Field>
            <Field label="Email">{account.email}</Field>
            <Field label="Status">
              <Badge variant={account.accountStatus === 'VERIFIED' ? 'success' : 'warning'}>
                {account.accountStatus}
              </Badge>
              {account.isDeleted && (
                <Badge variant="destructive" className="ml-1">
                  Deleted
                </Badge>
              )}
            </Field>
            <Field label="Created">{formatDateTime(account.createdAt)}</Field>
            <Field label="Usage intent">{account.usageIntent}</Field>
            <Field label="Personal members hidden">{account.hidePersonalMembers ? 'Yes' : 'No'}</Field>
            <Field label="Multi-factor">{account.isMfaEnabled ? 'On' : 'Off'}</Field>
            <Field label="Stripe customer">{account.stripeCustomerId ?? 'None'}</Field>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Members</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {groupByHub(account.members).map((group) => (
            <section key={group.hubId} className="space-y-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium">{group.hubName}</h2>
                {group.isPersonal && <Badge variant="secondary">Personal</Badge>}
                {!group.isPersonal && (
                  <div className="ml-auto">
                    <InviteMemberLever account={account} hubId={group.hubId} hubName={group.hubName} />
                  </div>
                )}
              </div>
              <ul className="divide-y border-t">
                {group.members.map((member) => (
                  <li key={member.memberId} className="flex items-center gap-2 py-2 text-sm">
                    <span>{member.email}</span>
                    <span className="text-xs text-muted-foreground">member {member.memberId}</span>
                    {member.isAdmin && <Badge variant="secondary">Admin</Badge>}
                    <Badge variant={member.isActive ? 'success' : 'warning'}>
                      {member.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    {member.isLoginLanding && <Badge variant="info">Landing</Badge>}
                    {member.inviteResendable && (
                      <div className="ml-auto">
                        <ResendInviteLever account={account} member={member} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Billing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Field label="Arrangement">
              <Badge variant={arrangementVariant[billing.arrangement]}>{billing.arrangement}</Badge>
            </Field>
            <Field label="Subscription">{billing.subscriptionStatus ?? 'None'}</Field>
            <Field label="Stripe subscription">
              {billing.stripeSubscriptionId && billing.stripeDashboardUrl ? (
                <a
                  href={billing.stripeDashboardUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4"
                >
                  {billing.stripeSubscriptionId}
                </a>
              ) : (
                billing.stripeSubscriptionId ?? 'None'
              )}
            </Field>
            <Field label="Stripe customer">
              {billing.stripeDashboardUrl ? (
                <a
                  href={billing.stripeDashboardUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4"
                >
                  {account.stripeCustomerId}
                </a>
              ) : (
                'None'
              )}
            </Field>
            <Field label="Period end">
              {billing.currentPeriodEnd ? formatDateTime(billing.currentPeriodEnd) : 'None'}
              {billing.cancelAtPeriodEnd && ' (cancels)'}
            </Field>
            <Field label="Balance">{formatAud(billing.balanceAud)}</Field>
            <Field label="Can spend on AI">{billing.canSpendLlm ? 'Yes' : 'No'}</Field>
            <Field label="Discount">
              {billing.discountPercent > 0 ? `${billing.discountPercent}%` : 'None'}
              {billing.discountNote && <span className="text-muted-foreground"> · {billing.discountNote}</span>}
            </Field>
          </dl>
          {!billing.discountAppliesToExistingSubscription && <StripeWarning />}
          <div className="flex flex-wrap gap-2 border-t pt-4">
            <SetDiscountLever account={account} />
            {billing.discountPercent > 0 && <RemoveDiscountLever account={account} />}
            {billing.arrangement === 'NONE' && <ActivateComplimentaryLever account={account} />}
            {billing.arrangement === 'COMPLIMENTARY' && <EndComplimentaryLever account={account} />}
            <GrantCreditLever account={account} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Wallet entries</CardTitle>
        </CardHeader>
        <CardContent>
          {account.walletEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No wallet entries yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance after</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {account.walletEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(entry.createdAt)}</TableCell>
                    <TableCell>{entry.kind}</TableCell>
                    <TableCell className="text-right">{formatAud(entry.amountAud)}</TableCell>
                    <TableCell className="text-right">{formatAud(entry.balanceAfterAud)}</TableCell>
                    <TableCell>{entry.note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Action log</CardTitle>
        </CardHeader>
        <CardContent>
          {account.actions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No operator actions on this account yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {account.actions.map((action) => (
                  <TableRow key={action.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(action.createdAt)}</TableCell>
                    <TableCell>{action.operatorEmail}</TableCell>
                    <TableCell className="whitespace-nowrap">{action.action}</TableCell>
                    <TableCell>{action.summary}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export function AccountPage() {
  const { id } = useParams();
  const accountId = Number(id);
  const account = useAccount(accountId);

  if (!Number.isInteger(accountId) || accountId <= 0) {
    return <Notice tone="destructive">Account id '{id}' is not valid.</Notice>;
  }
  if (account.isError) {
    return <Notice tone="destructive">{apiErrorMessage(account.error)}</Notice>;
  }
  if (account.isPending) {
    return <p className="text-sm text-muted-foreground">Loading account…</p>;
  }
  return <AccountDetailView account={account.data} />;
}
